// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson — https://benrichardson.dev
// See ADDITIONAL-TERMS.md for the section 7(b) attribution requirement.
//
// Realmfold — bootstrap and the screen machine. Owns which screen is up, the room's Net (created
// ONCE per session and never left for a rematch), and the setInterval that drives every clock. The
// rules live in rules.ts, the search in bot.ts, one match in session.ts, the tap machine in turn.ts
// and the board in render.ts.

import '@ben-gy/game-engine/mobile.css';
import './styles/main.css';

import { hardenViewport } from '@ben-gy/game-engine/mobile';
import { createSfx } from '@ben-gy/game-engine/sound';
import { createStore } from '@ben-gy/game-engine/storage';
import { createNet, roomAppId, setTurnConfig, type Net } from '@ben-gy/game-engine/net';
import { getTurnConfig } from '@ben-gy/game-engine/turn';
import { createRounds, type RoundInfo, type RoundPlayer, type Rounds } from '@ben-gy/game-engine/rematch';
import { clearRoomInUrl, createLobby, createRoomEntry, normalizeRoomCode, setRoomInUrl } from '@ben-gy/game-engine/lobby';
import { resolveName } from '@ben-gy/game-engine/identity';
import { newSeed } from '@ben-gy/game-engine/rng';

import { escapeHtml } from './dom';
import { MODE_IDS, modeOf, roundsIn } from './modes';
import { doubletOf } from './deck';
import { isBurnTurn, seatToPlay, slotRange, tallies, type RuleEvent, type State } from './rules';
import { DEFAULT_TIER, TIER_IDS, chooseMove, tierOf } from './bot';
import { Match, TURN_MS, type WireMsg } from './session';
import {
  demesneHtml,
  emptyHints,
  ghostOf,
  miniHtml,
  offersHtml,
  stripHtml,
  type BoardHints,
} from './render';
import {
  canPlace,
  confirmClaim,
  freshTurn,
  legalAnchors,
  lift,
  moveFor,
  offers,
  otherRotAnchors,
  promptFor,
  rotate,
  aim,
  type TurnUi,
} from './turn';
import { runCountdown } from './countdown';
import { ATTRIB, aboutHtml, helpHtml, menuHtml, resultsHtml, waitingHtml } from './ui';
import { SEAT_COLOUR, SEAT_NAME } from './palette';
import { PATCHES } from './cues';

const SLUG = 'realmfold';
const MAX_SEATS = 4;

const app = document.querySelector<HTMLElement>('#app')!;
let content: HTMLElement;

const store = createStore(SLUG);
// The custom patches MUST be passed here: play() on an unknown cue is a silent no-op, so a missing
// patch is an inaudible bug rather than an error. See cues.ts.
const sfx = createSfx({ muted: store.get<boolean>('muted', false) ?? false, patches: PATCHES });
const reducedMotion = typeof matchMedia === 'function' && matchMedia('(prefers-reduced-motion: reduce)').matches;

let unlocked = false;
function unlock(): void {
  if (unlocked) return;
  unlocked = true;
  try {
    sfx.unlock();
  } catch {
    /* audio unlock is best-effort */
  }
}
window.addEventListener('pointerdown', unlock, { once: true });
window.addEventListener('keydown', unlock, { once: true });

const playerName = resolveName(store, () => `Reeve ${Math.floor(Math.random() * 900 + 100)}`);
let myModeChoice = modeOf(store.get<string>('mode', '')).id;
let hostOptsMode = myModeChoice;
let myTier = tierOf(store.get<string>('tier', DEFAULT_TIER)).id;
let myPlayers = Math.min(MAX_SEATS, Math.max(2, store.get<number>('players', 2) ?? 2));

const bestKey = (id: string): string => `best_${id}`;
const bestFor = (id: string): number => store.get<number>(bestKey(id), 0) ?? 0;
function noteBest(id: string, score: number): void {
  if (score > bestFor(id)) store.set(bestKey(id), score);
}

let net: Net | null = null;
let rounds: Rounds | null = null;
let match: Match | null = null;
let roster: RoundPlayer[] = [];
let tally: number[] = [];
let tallyNames: string[] = [];
let roomCode = '';
let roundNo = 0;
let soloRound = 0;

// ── screen lifecycle ────────────────────────────────────────────────────────────
const cleanups: Array<() => void> = [];
const onCleanup = (fn: () => void): void => void cleanups.push(fn);
function teardown(): void {
  for (const fn of cleanups.splice(0)) {
    try {
      fn();
    } catch {
      /* a failing teardown must never block the next screen */
    }
  }
}
const setPlaying = (on: boolean): void => void document.body.classList.toggle('playing', on);

function mountShell(): void {
  app.innerHTML = `<main class="main-content" id="content"></main><footer class="site-footer">${ATTRIB}</footer>`;
  content = document.getElementById('content')!;
}

/**
 * A sheet has FOUR ways out — the close button, a tap outside, Escape, and a backdrop that is
 * genuinely reachable — because an overlay with one way out is a game you cannot play. The 350ms
 * arming delay is not a flourish: a sheet opened from a pointerup is otherwise closed instantly by
 * that same tap's trailing click.
 */
function sheet(html: string): void {
  const wrap = document.createElement('div');
  wrap.className = 'overlay';
  wrap.innerHTML = `<div class="sheet" role="dialog" aria-modal="true">${html}</div>`;
  document.body.appendChild(wrap);
  const armedAt = Date.now();
  const close = (): void => {
    wrap.remove();
    window.removeEventListener('keydown', onKey);
  };
  function onKey(e: KeyboardEvent): void {
    if (e.key === 'Escape') close();
  }
  wrap.addEventListener('pointerdown', (e) => {
    if (e.target === wrap && Date.now() - armedAt > 350) close();
  });
  wrap.querySelectorAll('.sheet-close').forEach((b) => b.addEventListener('click', close));
  window.addEventListener('keydown', onKey);
}

function toast(msg: string): void {
  const t = document.createElement('div');
  t.className = 'toast';
  t.textContent = msg;
  document.body.appendChild(t);
  setTimeout(() => t.classList.add('show'), 10);
  setTimeout(() => t.remove(), 1900);
}

function ping(cue: string, pitch?: number): void {
  try {
    sfx.play(cue, pitch === undefined ? undefined : { pitch });
  } catch {
    /* audio is best-effort and must never break a turn */
  }
}

// ── menu ────────────────────────────────────────────────────────────────────────
function showMenu(): void {
  teardown();
  setPlaying(false);
  content.innerHTML = menuHtml({ modeId: myModeChoice, tierId: myTier, best: bestFor(myModeChoice), muted: sfx.muted() });

  const seats = document.createElement('div');
  seats.className = 'picker small';
  seats.setAttribute('role', 'group');
  seats.setAttribute('aria-label', 'players');
  seats.innerHTML = [2, 3, 4]
    .map(
      (n) =>
        `<button class="chip${n === myPlayers ? ' on' : ''}" data-seats="${n}" aria-pressed="${n === myPlayers}">${n} players</button>`,
    )
    .join('');
  content.querySelector('.menu-actions')!.before(seats);

  content.querySelectorAll<HTMLElement>('.chip[data-mode]').forEach((c) =>
    c.addEventListener('click', () => {
      myModeChoice = modeOf(c.dataset.mode).id;
      store.set('mode', myModeChoice);
      ping('select');
      showMenu();
    }),
  );
  content.querySelectorAll<HTMLElement>('.chip[data-tier]').forEach((c) =>
    c.addEventListener('click', () => {
      myTier = tierOf(c.dataset.tier).id;
      store.set('tier', myTier);
      ping('select');
      showMenu();
    }),
  );
  content.querySelectorAll<HTMLElement>('.chip[data-seats]').forEach((c) =>
    c.addEventListener('click', () => {
      myPlayers = Math.min(MAX_SEATS, Math.max(2, Number(c.dataset.seats) || 2));
      store.set('players', myPlayers);
      ping('select');
      showMenu();
    }),
  );
  content.querySelector('#playSolo')!.addEventListener('click', () => startSolo());
  content.querySelector('#playFriends')!.addEventListener('click', () => showRoomEntry());
  content.querySelector('#help')!.addEventListener('click', () => sheet(helpHtml(modeOf(myModeChoice))));
  content.querySelector('#about')!.addEventListener('click', () => sheet(aboutHtml()));
  content.querySelector('#mute')!.addEventListener('click', () => {
    sfx.setMuted(!sfx.muted());
    store.set('muted', sfx.muted());
    showMenu();
  });

  if (!store.get<boolean>('seenHelp', false)) {
    store.set('seenHelp', true);
    sheet(helpHtml(modeOf(myModeChoice)));
  }
}

// ── the game screen ─────────────────────────────────────────────────────────────
interface Screen {
  destroy(): void;
}

let activeMatch: Match | null = null;
let activeUi: TurnUi = freshTurn();
let activeSubmit: ((move: number) => boolean) | null = null;

function buildGameScreen(m: Match, onQuit: () => void, onOver: () => void, onRestart: (() => void) | null): Screen {
  const mode = m.mode;
  content.innerHTML = `
    <section class="screen game">
      <div class="hud">
        <span class="hud-round" id="hudRound"></span>
        <span class="hud-mode">${escapeHtml(mode.name)}</span>
        <button class="iconbtn" id="pause" title="Pause" aria-label="Pause">❚❚</button>
      </div>
      <div class="strip-host" id="stripHost"></div>
      <div class="board-host" id="boardHost"></div>
      <div class="myline" id="myline"></div>
      <div class="clockbar" id="clockbar" hidden><i id="clockfill"></i></div>
      <p class="prompt" id="prompt" aria-live="polite"></p>
      <div class="offers-host" id="offersHost"></div>
      <div class="turn-actions" id="actions"></div>
    </section>`;

  const boardHost = content.querySelector<HTMLElement>('#boardHost')!;
  const stripHost = content.querySelector<HTMLElement>('#stripHost')!;
  const offersHost = content.querySelector<HTMLElement>('#offersHost')!;
  const hudRound = content.querySelector<HTMLElement>('#hudRound')!;
  const myline = content.querySelector<HTMLElement>('#myline')!;
  const promptEl = content.querySelector<HTMLElement>('#prompt')!;
  const actions = content.querySelector<HTMLElement>('#actions')!;

  let ui: TurnUi = freshTurn();
  activeUi = ui;
  let live = false;
  let destroyed = false;
  let fresh = new Set<number>();
  let botTimer: ReturnType<typeof setTimeout> | null = null;
  let paused = false;

  const viewSeat = (): number => m.mySeat ?? 0;

  const cd = runCountdown(boardHost, sfx, () => {
    if (destroyed) return;
    live = true;
    paint();
    maybeBot();
  });

  function hints(): BoardHints {
    const h = emptyHints();
    h.fresh = fresh;
    const seat = m.mySeat;
    h.interactive = live && m.isMyTurn && seat !== null;
    if (!h.interactive || seat === null) return h;
    if (ui.phase === 'placing') {
      h.legal = legalAnchors(m.state, seat, ui);
      h.legalRot = otherRotAnchors(m.state, seat, ui);
      h.ghost = ghostOf(m.state, seat, ui, canPlace(m.state, seat, ui));
    }
    return h;
  }

  function paint(): void {
    if (destroyed) return;
    const s = m.state;
    const seat = viewSeat();
    const scores = tallies(s).map((t) => t.total);

    hudRound.textContent = s.over ? 'Match over' : `Round ${Math.min(s.round, s.rounds)} of ${s.rounds}`;
    stripHost.innerHTML = stripHtml(s, m.names, scores, m.mySeat, seatToPlay(s));
    boardHost.querySelector('.demesne')?.remove();
    boardHost.insertAdjacentHTML('afterbegin', demesneHtml(s.boards[seat], s.side, hints()));
    myline.innerHTML = `<span class="mn" style="--seat:${SEAT_COLOUR[seat % SEAT_COLOUR.length]}">${escapeHtml(
      m.mySeat === null ? `${m.names[seat] ?? ''} (watching)` : m.names[seat] ?? 'You',
    )}</span><span class="ms">${scores[seat]}</span>`;

    promptEl.innerHTML = promptText();
    offersHost.innerHTML =
      s.over || m.fault !== null
        ? ''
        : offersHtml(
            offers(s, m.mySeat ?? -1, (st, n) => slotRange(st, n)),
            ui,
            m.names,
            isBurnTurn(s),
          );
    renderActions();
    renderClock();
  }

  function promptText(): string {
    const s = m.state;
    if (m.fault === 'build') return 'Someone here is on a different build of Realmfold — reload to play again.';
    if (m.fault === 'desync') return 'The tables stopped agreeing, so the match was stopped.';
    if (s.over) return '';
    if (!live) return '';
    if (m.mySeat === null) return 'Watching — you are in the next round.';
    if (!m.isMyTurn) return waitingHtml(m.names, seatToPlay(s), m.absent[seatToPlay(s)] ?? false);
    return escapeHtml(promptFor(s, m.mySeat, ui, mode));
  }

  function renderActions(): void {
    const s = m.state;
    const mine = m.isMyTurn && live;
    const burning = mine && isBurnTurn(s);
    const parts: string[] = [];
    if (mine && ui.pick !== null && ui.phase === 'lifted') {
      const doub = doubletOf(ui.pick);
      const spoils = doub && m.mySeat !== null && !legalAnchorsExist(doub, s, m.mySeat);
      parts.push(
        `<button class="btn primary big" id="claim">${spoils ? `Claim #${ui.pick} anyway` : `Claim #${ui.pick}`}</button>`,
      );
      parts.push('<button class="btn" id="drop">Back</button>');
    } else if (burning && ui.pick !== null) {
      parts.push(`<button class="btn primary big" id="doburn">Burn #${ui.pick}</button>`);
      parts.push('<button class="btn" id="drop">Back</button>');
    } else if (mine && ui.phase === 'placing') {
      const ok = m.mySeat !== null && canPlace(s, m.mySeat, ui);
      parts.push(`<button class="btn primary big" id="place"${ok ? '' : ' disabled'}>${ok ? 'Place it' : 'No match here'}</button>`);
      parts.push('<button class="btn" id="rot" aria-label="Rotate">⟳ Rotate</button>');
    }
    parts.push('<button class="btn ghost" id="quit">Quit</button>');
    actions.innerHTML = parts.join('');
    actions.querySelector('#claim')?.addEventListener('click', () => doClaim());
    actions.querySelector('#doburn')?.addEventListener('click', () => doBurn());
    actions.querySelector('#place')?.addEventListener('click', () => doPlace());
    actions.querySelector('#rot')?.addEventListener('click', () => {
      if (m.mySeat === null) return;
      rotate(m.state, m.mySeat, ui);
      ping('blip', 0.8);
      paint();
    });
    actions.querySelector('#drop')?.addEventListener('click', () => {
      ui = freshTurn();
      activeUi = ui;
      paint();
    });
    actions.querySelector('#quit')!.addEventListener('click', onQuit);
  }

  const legalAnchorsExist = (doub: ReturnType<typeof doubletOf>, s: State, seat: number): boolean =>
    !!doub && legalAnchors(s, seat, { phase: 'placing', pick: doub.n, cell: null, rot: 0 }).size > 0;

  const clockbar = content.querySelector<HTMLElement>('#clockbar')!;
  const clockfill = content.querySelector<HTMLElement>('#clockfill')!;

  function renderClock(): void {
    if (!m.networked || !m.isMyTurn || m.state.over) {
      clockbar.hidden = true;
      return;
    }
    clockbar.hidden = false;
    const frac = Math.max(0, Math.min(1, 1 - m.turnOpen / TURN_MS));
    clockfill.style.width = `${frac * 100}%`;
    clockbar.classList.toggle('low', frac < 0.25);
  }

  // `fresh` is derived HERE, from the event stream, and nowhere else. Both the local submit and the
  // remote receive path emit events, so this is the one place that sees every move whoever played
  // it — setting it on the local path instead leaves a peer watching their OWN last placement glow
  // while the opponent's goes unmarked.
  function juice(events: RuleEvent[]): void {
    for (const e of events) {
      if (e.kind === 'claim' && e.n !== undefined) {
        // Pitched by the doublet's number, so a fat pick literally sounds expensive.
        ping('claim', 0.75 + (e.n / 72) * 0.6);
      } else if (e.kind === 'place' && e.cells) {
        fresh = new Set(e.cells);
        ping('settle', 0.92 + Math.random() * 0.14);
        if ((e.coronets ?? 0) > 0) setTimeout(() => ping('coronet', 0.9 + (e.coronets ?? 0) * 0.12), 90);
      } else if (e.kind === 'merge') {
        setTimeout(() => ping('merge'), 60);
        if (!reducedMotion) boardHost.classList.add('pulse');
        setTimeout(() => boardHost.classList.remove('pulse'), 320);
      } else if (e.kind === 'discard') {
        ping('spoil');
        if (!reducedMotion) boardHost.classList.add('shake');
        setTimeout(() => boardHost.classList.remove('shake'), 320);
      } else if (e.kind === 'burn') {
        ping('burn');
      } else if (e.kind === 'over') {
        const rows = m.results();
        const mine = m.mySeat === null ? null : rows[m.mySeat];
        ping(mine?.won ? 'win' : 'lose');
      }
      if (!reducedMotion && 'vibrate' in navigator) {
        try {
          if (e.kind === 'place') navigator.vibrate(8);
          else if (e.kind === 'burn' || e.kind === 'discard') navigator.vibrate([12, 24, 12]);
        } catch {
          /* haptics are best-effort */
        }
      }
    }
  }

  function submit(move: number): boolean {
    if (move < 0) return false;
    const ok = m.submit(move);
    if (!ok) return false;
    ui = freshTurn();
    activeUi = ui;
    paint();
    maybeBot();
    return true;
  }
  activeSubmit = submit;

  function doClaim(): void {
    if (m.mySeat === null) return;
    const r = confirmClaim(m.state, m.mySeat, ui);
    if (r === 'spoil') {
      submit(moveFor(m.state, m.mySeat, ui));
      return;
    }
    if (r === 'placing') {
      ping('select');
      paint();
    }
  }

  function doBurn(): void {
    if (m.mySeat === null) return;
    submit(moveFor(m.state, m.mySeat, ui));
  }

  function doPlace(): void {
    if (m.mySeat === null) return;
    submit(moveFor(m.state, m.mySeat, ui));
  }

  // ONE delegated listener for the whole screen. The board and the row are rebuilt on every paint,
  // so per-element listeners would be re-attached dozens of times a round and leak on every one.
  function onTap(e: Event): void {
    if (!live || destroyed || paused || m.mySeat === null || !m.isMyTurn) return;
    const el = (e.target as HTMLElement).closest<HTMLElement>('[data-n],[data-c],[data-seat]');
    if (!el) return;
    if (el.dataset.seat !== undefined) {
      peek(Number(el.dataset.seat));
      return;
    }
    if (el.dataset.n !== undefined) {
      if (lift(m.state, m.mySeat, ui, Number(el.dataset.n))) {
        ping('lift');
        paint();
      }
      return;
    }
    if (el.dataset.c !== undefined && ui.phase === 'placing') {
      aim(m.state, m.mySeat, ui, Number(el.dataset.c));
      ping('blip', canPlace(m.state, m.mySeat, ui) ? 0.9 : 0.55);
      paint();
    }
  }
  content.addEventListener('click', onTap);

  /** Any rival's demesne, full size, from the strip. Read-only and dismissible four ways. */
  function peek(seat: number): void {
    const s = m.state;
    if (seat < 0 || seat >= s.players) return;
    const t = tallies(s)[seat];
    sheet(
      `<h2>${escapeHtml(m.names[seat] ?? '')}</h2>
       <p class="rsub">${t.total} points · ${t.coronets} coronets · largest region ${t.largest}</p>
       ${miniHtml(s.boards[seat], s.side, 'big')}
       <ul class="regions">${t.regions
         .slice(0, 8)
         .map((g) => `<li class="rg${g.points === 0 ? ' nil' : ''}">${g.cells} × ${g.coronets} <b>${g.points}</b></li>`)
         .join('')}</ul>
       <button class="btn sheet-close">Close</button>`,
    );
  }

  function maybeBot(): void {
    if (destroyed || paused || !live || m.state.over || !m.isBotTurn) return;
    if (botTimer !== null) return;
    // setTimeout, never rAF: a backgrounded tab throttles rAF to nothing and the opponent's turn
    // would simply never arrive.
    botTimer = setTimeout(() => {
      botTimer = null;
      if (destroyed || m.state.over) return;
      const seat = seatToPlay(m.state);
      const tier = m.bots[seat];
      if (!tier) return;
      submit(chooseMove(m.state, tier));
    }, 420);
  }

  // Measure the REAL elapsed time rather than trusting the interval. A backgrounded tab clamps
  // timers to about once a second, so feeding a hard-coded 250ms makes every clock run at a quarter
  // speed — a thirty-second turn clock takes two real minutes, which reads exactly like never.
  let lastTick = Date.now();
  const clock = setInterval(() => {
    if (destroyed) return;
    const now = Date.now();
    const dt = Math.min(5000, Math.max(0, now - lastTick));
    lastTick = now;
    // A SOLO pause really stops the clock. A networked match's clock is shared, so a local pause
    // must not stop it — otherwise pausing would freeze everyone else's game too.
    if (paused && !m.networked) return;
    m.tick(dt);
  }, 250);

  const isPaused = (): boolean => !!boardHost.querySelector('.pause-ov');

  function setPaused(on: boolean): void {
    let ov = boardHost.querySelector<HTMLElement>('.pause-ov');
    if (on) {
      if (ov) return;
      paused = true;
      if (botTimer !== null) {
        clearTimeout(botTimer);
        botTimer = null;
      }
      ov = document.createElement('div');
      ov.className = 'pause-ov';
      ov.innerHTML = `<div class="pause-card" role="dialog" aria-modal="true" aria-label="Paused">
        <h2>Paused</h2>
        <p class="pause-note">${
          m.networked ? 'The turn clock keeps running — the others are waiting.' : 'The table is held until you resume.'
        }</p>
        <button class="btn primary" id="resume">Resume</button>
        ${onRestart ? '<button class="btn" id="prestart">New match</button>' : ''}
        <button class="btn" id="phelp">How to play</button>
        <button class="btn ghost" id="pquit">Quit</button></div>`;
      boardHost.appendChild(ov);
      ov.querySelector('#resume')!.addEventListener('click', () => setPaused(false));
      ov.querySelector('#prestart')?.addEventListener('click', () => {
        setPaused(false);
        onRestart?.();
      });
      ov.querySelector('#phelp')!.addEventListener('click', () => sheet(helpHtml(mode)));
      ov.querySelector('#pquit')!.addEventListener('click', onQuit);
      // Route 2 of the dismissal contract: a tap anywhere off the card closes it. Armed on the next
      // frame so the tap that OPENED it cannot immediately close it again.
      setTimeout(() => {
        ov?.addEventListener('pointerdown', (ev) => {
          if (!(ev.target as HTMLElement).closest('.pause-card')) setPaused(false);
        });
      }, 350);
    } else {
      paused = false;
      ov?.remove();
      lastTick = Date.now();
      maybeBot();
    }
  }
  content.querySelector('#pause')!.addEventListener('click', () => setPaused(!isPaused()));

  function onKey(e: KeyboardEvent): void {
    if (m.mySeat === null) return;
    if (e.key === 'p' || e.key === 'P') setPaused(!isPaused());
    else if (e.key === 'Escape') {
      if (isPaused()) setPaused(false);
      else {
        ui = freshTurn();
        activeUi = ui;
        paint();
      }
    } else if (e.key === 'r' || e.key === 'R') {
      rotate(m.state, m.mySeat, ui);
      paint();
    } else if (e.key === 'Enter') {
      if (ui.phase === 'lifted') doClaim();
      else if (ui.phase === 'burning') doBurn();
      else if (ui.phase === 'placing') doPlace();
    } else if (/^[1-8]$/.test(e.key) && m.isMyTurn) {
      const n = m.state.reveal[Number(e.key) - 1];
      if (n !== undefined && lift(m.state, m.mySeat, ui, n)) {
        ping('lift');
        paint();
      }
    } else if (ui.phase === 'placing' && e.key.startsWith('Arrow')) {
      const s = m.state;
      const cur = ui.cell ?? 0;
      const d = e.key === 'ArrowLeft' ? -1 : e.key === 'ArrowRight' ? 1 : e.key === 'ArrowUp' ? -s.side : s.side;
      const next = cur + d;
      if (next >= 0 && next < s.side * s.side) {
        aim(s, m.mySeat, ui, next);
        paint();
      }
      e.preventDefault();
    }
  }
  window.addEventListener('keydown', onKey);

  m.deps.onChange = paint;
  m.deps.onEvents = (events) => juice(events);
  m.deps.onEnd = () => {
    setTimeout(() => {
      if (!destroyed) onOver();
    }, 900);
  };

  paint();

  return {
    destroy() {
      destroyed = true;
      clearInterval(clock);
      if (botTimer !== null) clearTimeout(botTimer);
      cd.cancel();
      content.removeEventListener('click', onTap);
      window.removeEventListener('keydown', onKey);
      if (activeSubmit === submit) activeSubmit = null;
    },
  };
}

// ── solo ────────────────────────────────────────────────────────────────────────
let soloScreen: Screen | null = null;

function startSolo(): void {
  teardown();
  setPlaying(true);
  soloRound++;
  const mode = modeOf(myModeChoice);
  const tier = tierOf(myTier);
  // Your seat rotates each match, so who opens the drafting rotates with it.
  const mySeat = (soloRound - 1) % myPlayers;
  const names: string[] = [];
  const bots: Array<ReturnType<typeof tierOf> | null> = [];
  for (let p = 0; p < myPlayers; p++) {
    if (p === mySeat) {
      names.push(playerName);
      bots.push(null);
    } else {
      // At a bigger table the seat name IS the name: three bots all called "Reeve Something"
      // truncate to "Reev…" in the 8-character strip and become indistinguishable.
      names.push(myPlayers > 2 ? SEAT_NAME[p] : tier.name);
      bots.push(tier);
    }
  }
  const m = new Match({
    modeId: mode.id,
    seed: newSeed(),
    players: myPlayers,
    round: soloRound,
    names,
    mySeat,
    bots,
    deps: { send: () => {}, onChange: () => {}, onEnd: () => {}, onEvents: () => {} },
  });
  match = m;
  activeMatch = m;
  tally = [];
  tallyNames = [];
  soloScreen = buildGameScreen(
    m,
    () => showMenu(),
    () => {
      noteBest(mode.id, m.results()[mySeat]?.tally.total ?? 0);
      showResults(false);
    },
    () => startSolo(),
  );
  onCleanup(() => soloScreen?.destroy());
}

// ── multiplayer ─────────────────────────────────────────────────────────────────
let netSend: (m: WireMsg) => void = () => {};
let mvOff: () => void = () => {};
let lobby: { repaint(): void; destroy(): void } | null = null;
let lobbyMounted = false;
let gameScreen: Screen | null = null;
let beat: ReturnType<typeof setInterval> | null = null;

const repaintLobby = (): void => {
  if (lobbyMounted) lobby?.repaint();
};

const seatOfPeer = (id: string): number => roster.slice(0, MAX_SEATS).findIndex((p) => p.id === id);

function showRoomEntry(): void {
  teardown();
  setPlaying(false);
  content.innerHTML = '<section class="screen"><div id="entry"></div></section>';
  const host = content.querySelector<HTMLElement>('#entry')!;

  const deep = new URL(location.href).searchParams.get('room');
  if (deep) {
    const code = normalizeRoomCode(deep);
    // Honoured ONCE, and cleared on the way in — otherwise a reload (or reopening from a home-screen
    // icon) silently drags the player back into a room they deliberately left.
    clearRoomInUrl();
    if (code.length >= 3) {
      void enterRoom(code, false);
      return;
    }
  }

  const entry = createRoomEntry({
    container: host,
    onSubmit: (code: string, created: boolean) => void enterRoom(normalizeRoomCode(code), created),
    onCancel: showMenu,
    title: 'Play with friends',
    subtitle: 'Start a room and send the link, or type a code you were given. Two to four players.',
  });
  onCleanup(() => entry.destroy());
}

async function enterRoom(code: string, created: boolean): Promise<void> {
  teardown();
  setPlaying(false);
  roomCode = code;
  setRoomInUrl(code);
  tally = [];
  tallyNames = [];
  match = null;
  activeMatch = null;

  content.innerHTML = '<section class="screen"><div class="lobby-searching"><span class="spinner"></span> Connecting…</div></section>';

  try {
    // ONCE at boot, before ANY mesh: Trystero pre-builds one global connection pool from the first
    // joinRoom's config, so a later call leaves the initiating half of every pair STUN-only.
    setTurnConfig(await getTurnConfig());
  } catch {
    /* fail open — TURN is an upgrade, never a dependency */
  }

  const n = createNet(
    { appId: roomAppId(SLUG), roomId: code, claimHost: created },
    {
      // Host transfer is genuinely a no-op here: the state is derived from (seed, mode, log), which
      // every peer already holds, so there is nothing to take over. It still re-broadcasts, and it
      // NEVER navigates — this fires after a round ends too, and a showMenu() here would eject
      // everyone from the room.
      onHostChange: () => {
        match?.onHostChanged();
        repaintLobby();
      },
      onPeers: () => repaintLobby(),
      onPeerLeave: (id) => {
        const seat = seatOfPeer(id);
        if (seat >= 0) match?.seatLeft(seat);
        repaintLobby();
      },
    },
  );
  net = n;

  const r = createRounds<{ mode: string }>({
    net: n,
    playerName,
    minPlayers: 2,
    roundOpts: () => ({ mode: myModeChoice }),
    onRound: (info) => startNetRound(info),
    onChange: (s) => {
      const ho = s.hostOpts as { mode?: string } | null;
      if (ho && typeof ho.mode === 'string') hostOptsMode = modeOf(ho.mode).id;
      repaintLobby();
      refreshResults();
    },
  });
  rounds = r;

  const send = n.channel<WireMsg>('mv', (data, peer) => match?.receive(data, seatOfPeer(peer)));
  netSend = (msg) => send(msg);
  mvOff = () => send.off();

  showLobby();
}

function showLobby(): void {
  const n = net;
  const r = rounds;
  if (!n || !r) return;
  lobbyMounted = true;
  setPlaying(false);

  content.innerHTML = '<section class="screen"><div id="lobby-host"></div></section>';
  const isHost = (): boolean => n.isHost() && n.hostSettled();

  lobby = createLobby({
    container: content.querySelector<HTMLElement>('#lobby-host')!,
    net: n,
    rounds: r,
    roomCode,
    minPlayers: 2,
    maxPlayers: MAX_SEATS,
    onCancel: () => void leaveRoom(),
    share: { title: 'Play Realmfold with me', text: (c: string) => `Join my Realmfold room: ${c}` },
    labels: { start: 'Deal the first row' },
    modeSlot: () => {
      if (isHost()) {
        return `<div class="lobby-modes" id="lm">${MODE_IDS.map(
          (id) =>
            `<button class="chip mode-btn${id === myModeChoice ? ' on' : ''}" data-mode="${id}" aria-pressed="${
              id === myModeChoice
            }">${escapeHtml(modeOf(id).name)}</button>`,
        ).join('')}<p class="mode-blurb">${escapeHtml(modeOf(myModeChoice).blurb)}</p></div>`;
      }
      // The host's pick is what the room plays, and it arrives GOSSIPED — rendering our own local
      // choice and labelling it the host's is a confident lie, and it has shipped once in this fleet.
      return `<p class="host-pick">${
        n.hostSettled() ? `The host chose <strong>${escapeHtml(modeOf(hostOptsMode).name)}</strong>` : 'Waiting for the host…'
      }</p>`;
    },
    onModeMount: () => {
      if (!isHost()) return;
      content.querySelectorAll<HTMLElement>('#lm .mode-btn').forEach((b) =>
        b.addEventListener('click', () => {
          myModeChoice = modeOf(b.dataset.mode).id;
          store.set('mode', myModeChoice);
          lobby?.repaint();
        }),
      );
    },
  });
  onCleanup(() => {
    lobby?.destroy();
    lobby = null;
  });
}

function startNetRound(info: RoundInfo<{ mode: string }>): void {
  teardown();
  lobbyMounted = false;
  roster = info.players;
  roundNo = info.round;
  const mode = modeOf(info.opts?.mode);
  // At most four seats play; anyone after them watches and is in the next round's roster. The slice
  // is load-bearing rather than defensive — sharing an invite link into a group chat is the ordinary
  // way a room fills, so a fifth arrival is the expected case.
  const seated = roster.slice(0, MAX_SEATS);
  const players = Math.max(2, seated.length);
  const mySeat = info.seated ? seated.findIndex((p) => p.id === (net?.selfId ?? '')) : -1;

  const names: string[] = [];
  for (let p = 0; p < players; p++) names.push(seated[p]?.name || SEAT_NAME[p]);
  // Two players CAN arrive with the same handle — the default one is random, and two tabs on one
  // device share the store outright. Identical names make a results screen unreadable.
  //
  // The duplicate set is worked out BEFORE any renaming. Renaming in place as you go is the obvious
  // way to write this and it only ever fixes the first of each pair: once "Reeve 858" has become
  // "Reeve 858 (Amber)", the second one no longer looks like a duplicate of anything. The two-tab
  // smoke test showed exactly that, as `Reeve 858 (Amber)` sitting opposite a bare `Reeve 858`.
  const seen = new Map<string, number>();
  for (const n of names) seen.set(n, (seen.get(n) ?? 0) + 1);
  for (let p = 0; p < players; p++) {
    if ((seen.get(names[p]) ?? 0) > 1) names[p] = `${names[p]} (${SEAT_NAME[p]})`;
  }
  if (tally.length !== players) {
    tally = new Array(players).fill(0);
    tallyNames = names.slice();
  }

  const m = new Match({
    modeId: mode.id,
    seed: info.seed,
    players,
    round: info.round,
    names,
    mySeat: mySeat >= 0 ? mySeat : null,
    bots: new Array(players).fill(null),
    networked: true,
    deps: { send: (msg) => netSend(msg), onChange: () => {}, onEnd: () => {}, onEvents: () => {} },
  });
  match = m;
  activeMatch = m;
  setPlaying(true);

  if (beat !== null) clearInterval(beat);
  // The heartbeat is what makes a lost message a non-event: it re-sends the whole log every 2s, and
  // it is also the liveness signal the absent-seat fill waits on.
  beat = setInterval(() => m.broadcast(), 2000);
  onCleanup(() => {
    if (beat !== null) clearInterval(beat);
    beat = null;
  });

  gameScreen = buildGameScreen(
    m,
    () => {
      // End this peer's round FIRST (phase playing → waiting), or the engine lobby paints nothing
      // for a peer still seated in a live round and the player gets a blank screen.
      rounds?.finish();
      backToLobby();
    },
    () => showResults(true),
    // No restart inside a networked round: every peer replays one shared log, so one side re-dealing
    // its own table is a desync by construction. The rematch after the round is how you play again.
    null,
  );
  onCleanup(() => {
    gameScreen?.destroy();
    gameScreen = null;
  });
  m.broadcast();
}

// ── results ─────────────────────────────────────────────────────────────────────
let resultsMounted = false;
let multi = false;

function showResults(isMulti: boolean): void {
  teardown();
  setPlaying(false);
  multi = isMulti;
  resultsMounted = true;
  const m = match;
  if (!m) {
    showMenu();
    return;
  }
  if (m.fault === null) {
    const rows = m.results();
    if (tally.length !== rows.length) {
      tally = new Array(rows.length).fill(0);
      tallyNames = rows.map((r) => r.name);
    }
    for (const r of rows) if (r.won) tally[r.seat]++;
  }
  if (isMulti) rounds?.finish();

  content.innerHTML = `
    <section class="screen results-screen" id="res"></section>
    <div class="menu-actions" id="ract"></div>`;
  renderResults();
  onCleanup(() => {
    resultsMounted = false;
  });
}

function renderResults(): void {
  const m = match;
  if (!m || !resultsMounted) return;
  const box = content.querySelector<HTMLElement>('#res');
  const act = content.querySelector<HTMLElement>('#ract');
  if (!box || !act) return;

  box.innerHTML = resultsHtml({
    rows: m.results(),
    boards: m.state.boards,
    side: m.state.side,
    mode: m.mode,
    players: m.state.players,
    tally,
    tallyNames,
    round: multi ? roundNo : soloRound,
    fault: m.fault,
  });

  act.innerHTML = multi
    ? `<button class="btn primary" id="again">Play again</button>
       <p class="waiting-note" id="waiting" aria-live="polite"></p>
       <button class="btn" id="lobby">Back to lobby</button>
       <button class="btn ghost" id="menu">Main menu</button>
       <button class="btn ghost results-feedback" id="feedback">Report a problem</button>`
    : `<button class="btn primary" id="again">Play again</button>
       <button class="btn" id="share">Share</button>
       <button class="btn ghost" id="menu">Main menu</button>
       <button class="btn ghost results-feedback" id="feedback">Report a problem</button>`;

  act.querySelector('#feedback')!.addEventListener('click', (e) =>
    window.feedback?.open({ returnFocusTo: e.currentTarget as HTMLElement }),
  );
  act.querySelector('#menu')!.addEventListener('click', () => (multi ? void leaveRoom() : showMenu()));
  if (multi) {
    const again = act.querySelector<HTMLButtonElement>('#again')!;
    again.addEventListener('click', () => {
      rounds?.vote();
      again.disabled = true;
      again.textContent = 'Waiting for the others…';
      refreshResults();
    });
    act.querySelector('#lobby')!.addEventListener('click', () => backToLobby());
    refreshResults();
  } else {
    act.querySelector('#again')!.addEventListener('click', () => startSolo());
    act.querySelector('#share')!.addEventListener('click', () => void share());
  }
}

function refreshResults(): void {
  if (!resultsMounted || !multi) return;
  const waiting = content.querySelector<HTMLElement>('#waiting');
  if (!waiting) return;
  const r = rounds?.state();
  if (r && r.startsInMs !== null) {
    waiting.textContent = `Next round in ${Math.ceil(r.startsInMs / 1000)}s — ${r.votes.length}/${Math.max(r.present.length, 1)} ready`;
  } else if (r && r.votes.length) {
    waiting.textContent = `${r.votes.length}/${Math.max(r.present.length, 1)} ready for another round`;
  } else {
    waiting.textContent = '';
  }
}

setInterval(() => refreshResults(), 400);

async function share(): Promise<void> {
  const m = match;
  if (!m || m.mySeat === null) return;
  const mine = m.results()[m.mySeat];
  const text = `Realmfold · ${m.mode.name} — a demesne worth ${mine.tally.total}, ${mine.tally.coronets} coronets, largest region ${mine.tally.largest}\nhttps://realmfold.benrichardson.dev`;
  try {
    if (navigator.share) {
      await navigator.share({ text });
      return;
    }
  } catch {
    /* cancelled — fall through to copy */
  }
  try {
    await navigator.clipboard.writeText(text);
    toast('Copied to clipboard');
  } catch {
    toast('Could not copy');
  }
}

function backToLobby(): void {
  teardown();
  match = null;
  activeMatch = null;
  if (net && rounds) showLobby();
  else showMenu();
}

async function leaveRoom(): Promise<void> {
  teardown();
  clearRoomInUrl();
  lobbyMounted = false;
  match = null;
  activeMatch = null;
  const n = net;
  const r = rounds;
  net = null;
  rounds = null;
  netSend = () => {};
  try {
    mvOff();
  } catch {
    /* the channel may already be gone */
  }
  mvOff = () => {};
  r?.destroy();
  if (n) {
    try {
      await n.leave();
    } catch {
      /* leaving is best-effort */
    }
  }
  showMenu();
}

// ── boot ────────────────────────────────────────────────────────────────────────
function boot(): void {
  hardenViewport();
  mountShell();
  const room = new URL(location.href).searchParams.get('room');
  if (room && normalizeRoomCode(room).length >= 3) showRoomEntry();
  else showMenu();
  window.addEventListener('beforeunload', () => {
    void net?.leave();
  });
}

// Exposed so the browser-verification pass can drive the REAL submit path synchronously — rAF is
// throttled in a hidden tab, and every phone check runs through this. Never read by gameplay.
(window as unknown as { __realmfold?: unknown }).__realmfold = {
  state(): unknown {
    const m = activeMatch;
    if (!m) return null;
    const s = m.state;
    return {
      mode: s.modeId,
      side: s.side,
      players: s.players,
      round: s.round,
      rounds: s.rounds,
      slot: s.slot,
      turn: seatToPlay(s),
      burnTurn: isBurnTurn(s),
      mySeat: m.mySeat,
      over: s.over,
      fault: m.fault,
      reveal: s.reveal.slice(),
      claimedBy: s.claimedBy.slice(),
      scores: tallies(s).map((t) => t.total),
      discards: s.discards.slice(),
      burned: s.burned.slice(),
      board: Array.from(s.boards[m.mySeat ?? 0].terr),
      log: s.log.slice(),
      names: m.names.slice(),
      absent: m.absent.slice(),
    };
  },
  ui: () => ({ ...activeUi }),
  /** Play one legal move with the shipped search — enough to walk a whole match in a pane. */
  auto(): boolean {
    const m = activeMatch;
    if (!m || m.state.over) return false;
    const move = chooseMove(m.state, tierOf('reeve'));
    return move >= 0 && !!activeSubmit && activeSubmit(move);
  },
  /** Walk the match to its end, so the results screen can be reached without 48 taps. */
  finish(limit = 200): number {
    let n = 0;
    const m = activeMatch;
    while (m && !m.state.over && n < limit) {
      const move = chooseMove(m.state, tierOf('reeve'));
      if (move < 0 || !activeSubmit || !activeSubmit(move)) break;
      n++;
    }
    return n;
  },
  seats: MAX_SEATS,
  colours: SEAT_COLOUR,
  tiers: TIER_IDS,
  modes: MODE_IDS,
  roundsIn,
};

boot();
